<template>
    <div class="grid gap-y-6" v-if="status == 'success'">
        <div class="grid gap-y-4">
            <div class="bg-[#f5f5f5] dark:bg-[#202226] rounded-md px-6 py-6">
                <div class="grid grid-cols-1 xl:grid-cols-2 gap-10">
                    <div>
                        <div class="flex items-center gap-x-2.5">
                            <h3 class="dark:text-white text-lg font-semibold">{{ skin?.name }}</h3>
                            <div>
                                <ShadBadge v-if="$dayjs(skin?.created_at).isAfter($dayjs().subtract(10, 'day')) && $dayjs(skin?.created_at).isBefore($dayjs())" variant="success">New</ShadBadge>
                            </div>
                        </div>
                        <div class="grid place-items-center min-h-full">
                            <NuxtImg :src="skin?.icon" class="mt-4 mx-auto h-36 object-contain" />
                        </div>
                    </div>
                    <div class="flex items-center bg-[#ededed] dark:bg-[#2c2e32] dark:text-white py-4 pl-4 pr-6 rounded-md">
                        <table class="table w-full">
                            <tbody>
                                <tr>
                                    <td class="py-1 font-semibold flex gap-x-2 items-center justify-start">
                                        <i class="fa-solid fa-circle-question w-7 text-center"></i> Category
                                    </td>
                                    <td class="py-1 font-medium text-right">
                                        {{ skin?.category }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="py-1 font-semibold flex gap-x-2 items-center justify-start">
                                        <i class="fa-solid fa-scale-balanced w-7 text-center"></i> Demand
                                    </td>
                                    <td class="py-1 font-medium text-right">
                                        {{ skin?.demand }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="py-1 font-semibold flex gap-x-2 items-center justify-start">
                                        <i class="fa-solid fa-hand-holding-dollar w-7 text-center"></i> Value
                                    </td>
                                    <td class="py-1 font-medium text-right">
                                        {{ skin?.value != undefined ? formatNumber(skin?.value, 0, true) : 'N/A' }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="py-1 font-semibold flex gap-x-2 items-center justify-start">
                                        <i class="fa-solid fa-exchange w-7 text-center"></i> Tradeable?
                                    </td>
                                    <td class="py-1 font-medium text-right">
                                        {{ skin?.tradeable ? 'Yes' : 'No' }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="py-1 font-semibold flex gap-x-2 items-center justify-start">
                                        <i class="fa-solid fa-calendar-days w-7 text-center"></i> Date Added
                                    </td>
                                    <td class="py-1 font-medium text-right">
                                        {{ $dayjs(skin?.created_at).format('DD/MM/YYYY') }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="py-1 font-semibold flex gap-x-2 items-center justify-start">
                                        <i class="fa-solid fa-calendar-days w-7 text-center"></i> Last Updated
                                    </td>
                                    <td class="py-1 font-medium text-right">
                                        {{ $dayjs(skin?.updated_at).format('DD/MM/YYYY') }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div class="bg-[#f5f5f5] dark:bg-[#202226] rounded-md px-6 py-6" v-if="valueChanges?.length! > 0">
                <h3 class="dark:text-white text-md font-semibold">Value Changes</h3>
                <ClientOnly>
                    <apexchart type="area" :options="chartOptions" :series="series" />
                </ClientOnly>
            </div>
        </div>
        <div v-if="similarSkins?.data.length! > 0">
            <h3 class="dark:text-white text-lg font-semibold">Similar Skins</h3>
            <div class="mt-4 grid grid-cols-2 sm:auto-fill-[160px] md:auto-fill-[190px] gap-3">
                <NuxtLink :to="`/da-hood/skin/${skin?.id}`" v-for="skin in similarSkins?.data" :key="skin?.id">
                    <Skin class="mt-0" game="da-hood" :icon="skin?.icon" :name="skin?.name" :category="skin?.category" :value="skin?.value" :date="skin?.created_at" />
                </NuxtLink>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { SkinData as SimilarSkin } from '../index.vue'
import Skin from '../components/Skin.vue'

interface SkinData {
    id: number;
    name: string;
    icon: string;
    category: string;
    demand: string;
    value: number;
    tradeable: boolean;
    created_at: string;
    updated_at: string;
}

const { data: skin, status, error } = await useFetch<SkinData>(`/api/da-hood/skin/${useRoute().params.id}`)

if (error.value) {
    throw createError({
        statusCode: 404,
        fatal: true
    })
}

const { data: valueChanges } = await useFetch<{ date: string; newValue: number }[]>(`/api/da-hood/skin/${skin.value?.id}/value-changes`)

const series = ref([{
    name: 'Value',
    data: valueChanges.value?.map((item) => ({
        x: new Date(item.date).getTime(),
        y: item.newValue,
    }))
}])

const chartOptions = {
    grid: {
        show: false
    },
    chart: {
        foreColor: '#ffffff',
        fontFamily: 'Montserrat, sans-serif',
        toolbar: {
            show: false
        }
    },
    colors: ['#eab308'],
    stroke: {
        curve: 'smooth',
        width: 3
    },
    markers: {
        size: 6
    },
    fill: {
        type: 'gradient',
        gradient: {
            shadeIntensity: 0.5,
            opacityFrom: 0.7,
            opacityTo: 0.9,
            stops: [0, 100]
        }
    },
    dataLabels: {
        enabled: false
    },
    xaxis: {
        type: 'datetime',
        labels: {
            style: {
                colors: '#000000',
                fontFamily: 'Montserrat, sans-serif',
            },
            // Formatter to display the date in "Month Day" format
            formatter: function (value: number) {
                return new Date(value).toLocaleDateString('en-US', {
                    month: 'long', // e.g., "September"
                    day: 'numeric' // e.g., "16"
                });
            }
        }
    },
    yaxis: {
        opposite: true,
        labels: {
            style: {
                colors: ['#000000'],
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px'
            }
        }
    },
    tooltip: {
        enabled: true,
        theme: 'dark',
        style: {
            fontSize: '14px',
            fontFamily: 'Montserrat, sans-serif',
        },
        y: {
            formatter: function (val: any) {
                return formatNumber(val, 0, true);
            }
        }
    }
}

interface ApiResponse {
    data: SimilarSkin[];
    currentPage: number;
    isFirstPage: boolean;
    isLastPage: boolean;
    previousPage: number | null;
    nextPage: number | null;
    pageCount: number;
    totalCount: number;
}

const { data: similarSkins } = await useFetch<ApiResponse>('/api/da-hood/skins', {
    query: {
        category: [skin.value?.category],
        exclude: skin.value?.id,
        page: 1,
        limit: 10
    }
})

useSeoMeta({
    title: `Skin - ${skin.value?.name}`
})

definePageMeta({
    validate: async (route) => {
        return typeof route.params.id === 'string' && /^\d+$/.test(route.params.id)
    }
})
</script>